import {
  getDeleteApiUserInstitutionIdUserIdMutationOptions,
  NormalizedApiError,
  type UserDTO,
} from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Avatar, Badge, Icon, openModal } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './Users.module.css';
import { userDisplayName, userKeyOf, usersColumnText } from './usersModel';

import { useInstitutionOptions } from '@/hooks/useInstitutionOptions';
import { useResetCertification } from '@/hooks/useResetCertification';
import { MIN_INSTITUTIONS_TO_MOVE } from '@/ui/pages/UserMove/userMoveModel';
import { MobileCardActions, type RowActionEntry } from '@/ui/shared/RowActions';
import { EMPTY_VALUE } from '@/utils/emptyValue';

/** HS-1375: on `DELETE /api/User/{institutionId}/{userId}`, 400 means exactly one thing —
 * blocked by a dependent row (FK violation) — never a request-shape problem. */
const STATUS_BAD_REQUEST = 400;

function MobileCard({ row }: { row: UserDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // HS-869: the card/menu read the DTO the list query returns.
  const { id, tenantId } = userKeyOf(row);
  const name = userDisplayName(row);
  // HS-847: delete via server-ops (composite institutionId/userId key, per the
  // generated contract) — invalidates the user list query + toast.
  const del = useEntityDelete({
    entity: 'user',
    mutationOptions: getDeleteApiUserInstitutionIdUserIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
    // HS-1375: see UserDetails.tsx — a dependency-blocked delete (400) gets a title that
    // actually names the situation instead of the generic `errors.validation` copy; the raw
    // `detail` text still shows as the toast's description, unchanged.
    errorTitleKey: (error) =>
      error instanceof NormalizedApiError && error.status === STATUS_BAD_REQUEST
        ? 'users.delete.blockedTitle'
        : undefined,
  });
  // HS-1288: the same rule as the desktop menu — with one institution there is no destination.
  const { options: institutionOptions } = useInstitutionOptions();
  const resetCertification = useResetCertification();
  const canMove = institutionOptions.length >= MIN_INSTITUTIONS_TO_MOVE;

  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally
  // rendered dialog. It resolves with the pressed action's `value`, or `null` when
  // dismissed via the overlay, Escape or the close button.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('users.deleteTitle', { name }),
      description: t('users.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ institutionId: tenantId, userId: id });
  }, [del, id, name, tenantId, t]);

  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      // Matches Users/RowActions.tsx: PUT /api/User/{institutionId}/{userId} carries no
      // permission attribute at all, so this is deliberately stricter than the API —
      // User.Update is the seeded key for the intent.
      permission: 'user.edit',
      icon: 'edit-01',
      onClick: () => {
        void navigate({
          to: '/users/$id/$tenantId/edit',
          params: { id, tenantId },
        });
      },
    },
    null,
    {
      label: t('users.actions.manageScope'),
      permission: 'user.manageScopes',
      icon: 'bank',
      onClick: () => {
        void navigate({
          to: '/users/$id/$tenantId/access',
          params: { id, tenantId },
        });
      },
    },
    ...(canMove
      ? [
          {
            label: t('users.actions.moveUser'),
            icon: 'switch-horizontal-01' as const,
            onClick: () => {
              void navigate({
                to: '/users/$id/$tenantId/move',
                params: { id, tenantId },
              });
            },
          },
        ]
      : []),
    {
      label: t('users.actions.managePermissions'),
      permission: 'user.managePermissions',
      icon: 'users-02',
      onClick: () => {
        void navigate({
          to: '/users/$id/$tenantId/permissions',
          params: { id, tenantId },
        });
      },
    },
    {
      // Mirrors Users/RowActions.tsx, including the all-scopes reset (HS-1491) and its copy — the
      // two menus are hand-duplicated on this page, so they drift unless changed together.
      label: t('users.actions.resetCertification'),
      permission: 'certification.reset',
      icon: 'refresh-cw-05',
      onClick: () => {
        void resetCertification({
          userId: id,
          institutionId: tenantId,
          allScopes: true,
          name,
          description: t('certifications.reset.descriptionAll'),
        });
      },
    },
    null,
    {
      label: t('common.delete'),
      permission: 'user.delete',
      icon: 'trash-01',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return (
    <div className={styles['m-card']}>
      <Link to="/users/$id/$tenantId" params={{ id, tenantId }} className={styles['m-card-title']}>
        <span className={styles['m-badge-avatar']}>
          <Avatar name={name} size="sm" />
          {name}
        </span>
      </Link>
      <div className={styles['m-card-badges']}>
        {row.userIdentifier != null && row.userIdentifier !== '' && (
          <Badge variant="modern" size="sm" iconLeading={<Icon name="hash-02" />}>
            {row.userIdentifier}
          </Badge>
        )}
        <Badge variant="modern" size="sm" iconLeading={<Icon name="bank" />}>
          {row.institution?.name ?? row.institutionId}
        </Badge>
        {row.isCertified === true ? (
          <Badge variant="modern" size="sm" color="success" iconLeading={<Icon name="check" />}>
            {t('users.certified')}
          </Badge>
        ) : (
          <Badge variant="modern" size="sm" color="error" iconLeading={<Icon name="x-close" />}>
            {t('users.uncertified')}
          </Badge>
        )}
        <Badge variant="modern" size="sm" iconLeading={<Icon name="mail-01" />}>
          {row.email}
        </Badge>
      </div>
      {/* HS-1565 slice 2: the base role, labelled like the table column. */}
      <div className={styles['m-card-meta']}>
        <span className={styles['m-card-meta-label']}>{t('users.columns.role')}</span>
        <span className={styles['m-card-meta-value']}>
          {usersColumnText.role(row) || EMPTY_VALUE}
        </span>
      </div>
      <div className={styles['m-card-meta']}>
        <span className={styles['m-card-meta-label']}>{t('users.columns.deactivationDate')}</span>
        <span className={styles['m-card-meta-value']}>{row.activeUntil}</span>
      </div>
      <MobileCardActions items={items} />
    </div>
  );
}

export default MobileCard;
